import React from 'react';
import AppStatic, { isStudentsBuild } from '../config/AppStatic';
import Card from './ui/Card';
import Button from './ui/Button';
import MultiSelectDropdown from './ui/MultiSelectDropdown'; // Keep this import
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import MobileFilterButtons from './MobileFilterButtons';
import StudentGroupSelectionModal from './StudentGroupSelectionModal';
import TeacherSelectionModal from './TeacherSelectionModal';

function ScheduleFilter({
  // Filter options
  teachers,
  courses,
  groups, // Keep groups prop
  // Current filter values
  selectedTeacher,
  selectedCourse,
  selectedGroups, // Keep selectedGroups prop
  onTeacherChange,
  onCourseChange,
  onGroupChange, // Keep onGroupChange prop
  onClearFilters,
  isPublicView = false, // NEW: Add isPublicView prop
  onOpenComplexFilter, // NEW: Add complex filter handler
  // Complex filter data
  complexFilterGroups = [],
  complexFilterTeachers = [],
  complexFilterSubjects = [],
  complexFilterRooms = [],
  // Visibility controls
  showTeacher = true,
  showCourse = !isPublicView,
  showGroups = true,
  compactMobile = false
}) {
  // Mobile modal state
  const [isStudentModalOpen, setIsStudentModalOpen] = React.useState(false);
  const [isTeacherModalOpen, setIsTeacherModalOpen] = React.useState(false);
  // Ensure callbacks are functions to prevent errors
  const handleTeacherChange = (e) => typeof onTeacherChange === 'function' && onTeacherChange(e.target.value);
  const handleCourseChange = (e) => typeof onCourseChange === 'function' && onCourseChange(e.target.value);

  // Handle group changes based on view mode
  const handleGroupsChange = (value) => {
    if (typeof onGroupChange === 'function') {
      if (isPublicView) {
        // In public view, value is a single string from select dropdown
        onGroupChange(value);
      } else {
        // In admin view, value is an array from MultiSelectDropdown
        onGroupChange(value);
      }
    }
  };

  const handleClearFilters = () => typeof onClearFilters === 'function' && onClearFilters();
  const handleOpenComplexFilter = () => typeof onOpenComplexFilter === 'function' && onOpenComplexFilter();

  // Mobile modal handlers
  const handleOpenStudentModal = () => setIsStudentModalOpen(true);
  const handleCloseStudentModal = () => setIsStudentModalOpen(false);
  const handleOpenTeacherModal = () => setIsTeacherModalOpen(true);
  const handleCloseTeacherModal = () => setIsTeacherModalOpen(false);

  const handleStudentGroupSelect = (selectedGroups) => {
    if (typeof onGroupChange === 'function') {
      // For public view (mobile), pass the first selected group as a string
      // For admin view, pass the array as-is
      if (isPublicView && selectedGroups && selectedGroups.length > 0) {
        onGroupChange(selectedGroups[0]); // Pass the first group as a string
      } else {
        onGroupChange(selectedGroups); // Pass the array for admin view
      }
    }
  };

  const handleTeacherSelect = (selectedTeacher) => {
    if (typeof onTeacherChange === 'function') {
      onTeacherChange(selectedTeacher);
    }
  };

  // Ensure options are arrays before mapping
  const teacherOptions = Array.isArray(teachers) ? teachers : [];
  const courseOptions = Array.isArray(courses) ? courses : [];
  const groupOptions = Array.isArray(groups) ? groups : [];

  // Check if complex filters are active
  const complexFiltersActive = complexFilterGroups.length > 0 ||
    complexFilterTeachers.length > 0 ||
    complexFilterSubjects.length > 0 ||
    complexFilterRooms.length > 0;

  // Helper function to generate complex filter text overview
  const getComplexFilterText = () => {
    const parts = [];

    if (complexFilterGroups.length > 0) {
      const groupsText = complexFilterGroups.length > 3
        ? complexFilterGroups.slice(0, 3).join(', ') + '...'
        : complexFilterGroups.join(', ');
      parts.push(`Groups: ${groupsText}`);
    }
    if (complexFilterTeachers.length > 0) {
      const teachersText = complexFilterTeachers.length > 3
        ? complexFilterTeachers.slice(0, 3).join(', ') + '...'
        : complexFilterTeachers.join(', ');
      parts.push(`Teachers: ${teachersText}`);
    }
    if (complexFilterSubjects.length > 0) {
      const subjectsText = complexFilterSubjects.length > 3
        ? complexFilterSubjects.slice(0, 3).join(', ') + '...'
        : complexFilterSubjects.join(', ');
      parts.push(`Subjects: ${subjectsText}`);
    }
    if (complexFilterRooms.length > 0) {
      const roomsText = complexFilterRooms.length > 3
        ? complexFilterRooms.slice(0, 3).join(', ') + '...'
        : complexFilterRooms.join(', ');
      parts.push(`Rooms: ${roomsText}`);
    }

    return parts.join(' | ');
  }; // Keep group options
  const appType = AppStatic.getBuildType();

  // Auto-open student group selection on mobile in STUDENTS app when no group is selected
  React.useEffect(() => {
    if (isPublicView && appType === 'STUDENTS') {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const hasGroup = Array.isArray(selectedGroups) && selectedGroups.length > 0 && selectedGroups[0];
      if (isMobile && !hasGroup && !isStudentModalOpen) {
        setIsStudentModalOpen(true);
      }
      if (isMobile && hasGroup && isStudentModalOpen) {
        setIsStudentModalOpen(false);
      }
    }
  }, [isPublicView, appType, selectedGroups, isStudentModalOpen]);

  return (
    <>
      {/* Mobile View - Show mobile filter buttons on small screens */}
      <div className="block md:hidden">
        <MobileFilterButtons
          selectedTeacher={selectedTeacher}
          selectedGroups={selectedGroups}
          onOpenStudentModal={handleOpenStudentModal}
          onOpenTeacherModal={handleOpenTeacherModal}
          onClearFilters={handleClearFilters}
          teachers={teacherOptions}
          groups={groupOptions}
          showTeacher={showTeacher}
          showStudent={showGroups && !(isPublicView && isStudentsBuild())}
          compact={compactMobile}
        />
      </div>

      {/* Desktop View - Show traditional filters on medium screens and up */}
      <div className="hidden md:flex items-center gap-3">
        {/* Teacher Filter - Label Removed */}
        {showTeacher && (
          <div className={isPublicView ? "w-2/3" : "w-1/2"}>
            {complexFiltersActive ? (
              <div className="w-full px-4 py-3  rounded-md shadow-sm bg-gray-50 text-base text-gray-700 min-h-[48px] flex items-center">
                {getComplexFilterText()}
              </div>
            ) : (
              <select
                id="teacher-filter"
                value={selectedTeacher || ''}
                onChange={handleTeacherChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base min-h-[48px]"
                aria-label="Filter by Teacher"
              >
                <option value="">All Teachers</option>
                {teacherOptions.filter(t => t).map(teacher => {
                  const teacherName = typeof teacher === 'string' ? teacher : teacher?.name || 'Unknown';
                  const teacherKey = typeof teacher === 'string' ? teacher : teacher?.id || teacher?.name || 'Unknown';
                  return <option key={teacherKey} value={teacherName}>{teacherName}</option>;
                })}
              </select>
            )}
          </div>
        )}

        {/* Course Filter - Only show for admin view */}
        {showCourse && (
          <div className="w-1/3">
            <select
              id="course-filter"
              value={selectedCourse || ''}
              onChange={handleCourseChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              aria-label="Filter by Course"
            >
              <option value="">All Courses</option>
              {courseOptions.filter(c => c).map(course => {
                const courseName = typeof course === 'string' ? course : course?.name || 'Unknown';
                const courseKey = typeof course === 'string' ? course : course?.id || course?.name || 'Unknown';
                return <option key={courseKey} value={courseName}>{courseName}</option>;
              })}
            </select>
          </div>
        )}

        {/* Group Filter - Conditional rendering based on view mode */}
        {showGroups && (
          <div className={isPublicView ? "w-3/4" : "w-1/3"}>
            {isPublicView ? (
              // Single-select dropdown for public view
              <select
                id="group-filter"
                value={selectedGroups?.[0] || ''}
                onChange={(e) => handleGroupsChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                aria-label="Filter by Group"
              >
                <option value="">All Groups</option>
                {groupOptions.filter(g => g).map(group => {
                  const groupName = typeof group === 'string' ? group : group?.name || 'Unknown';
                  const groupKey = typeof group === 'string' ? group : group?.id || group?.name || 'Unknown';
                  return <option key={groupKey} value={groupName}>{groupName}</option>;
                })}
              </select>
            ) : (
              // Multi-select dropdown for admin view
              <>
                <span id="group-filter-label" className="sr-only">Filter by Group(s)</span>
                <MultiSelectDropdown
                  options={groupOptions.filter(g => g).map(group => typeof group === 'string' ? group : group?.name || 'Unknown')}
                  selectedOptions={selectedGroups}
                  onChange={handleGroupsChange}
                  placeholder="All Groups"
                  aria-labelledby="group-filter-label"
                />
              </>
            )}
          </div>
        )}

        {/* Action Buttons - Pushed to the right */}
        <div className="flex items-center gap-2">
          {/* Complex Filter Button - Hide only for students view */}
          {!isStudentsBuild() && (
            <button
              onClick={handleOpenComplexFilter}
              className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded-full"
              aria-label="Open advanced filters"
              title="Advanced Filters"
            >
              <HiAdjustmentsHorizontal className="h-5 w-5" />
            </button>
          )}

          {/* Clear Button */}
          <button
            onClick={handleClearFilters}
            className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded-full"
            aria-label="Clear all filters"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Modals */}
      <StudentGroupSelectionModal
        isOpen={isStudentModalOpen}
        onClose={handleCloseStudentModal}
        groups={groupOptions}
        selectedGroups={selectedGroups}
        onGroupSelect={handleStudentGroupSelect}
      />

      <TeacherSelectionModal
        isOpen={isTeacherModalOpen}
        onClose={handleCloseTeacherModal}
        teachers={teacherOptions}
        selectedTeacher={selectedTeacher}
        onTeacherSelect={handleTeacherSelect}
      />
    </>
  );
}

export default ScheduleFilter;